import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { SuiteShellProvider, SuiteShell } from '@levelup/ui-shell';
import { useAuth } from './hooks/useAuth';
import { BootGate } from './components/BootGate';
import { DashboardPage } from './pages/DashboardPage';
import { RecommendationsPage } from './pages/RecommendationsPage';
import { PlansPage } from './pages/PlansPage';
import { AlertsPage } from './pages/AlertsPage';
import { AgentsPage } from './pages/AgentsPage';
import { ChatPage } from './pages/ChatPage';
import { SettingsPage } from './pages/SettingsPage';
import { OffloadResponsibilitiesPage } from './pages/OffloadResponsibilitiesPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { CentralDiagnosticsPage } from './pages/CentralDiagnosticsPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { DocumentDetailPage } from './pages/DocumentDetailPage';
import { BeneficiaryReviewPage } from './pages/BeneficiaryReviewPage';
import { LoadingSpinner } from './components/ui/LoadingSpinner';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { DevWhoAmI } from './components/DevWhoAmI';
import { SHELL_ADAPTER } from './shell/SuiteShellAdapter';
import { startLogin, isBackendConfigured } from './lib/centralAuth';
import { useCentralEventStream } from './hooks/useCentralEventStream';
import { useNotificationSSE } from './hooks/useNotificationSSE';

function LoginPage() {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isBackendConfigured()) {
      setError('Backend API URL not configured. Set VITE_API_URL in your environment.');
      return;
    }

    startLogin().catch((err) => {
      setError(err instanceof Error ? err.message : 'Failed to start login');
    });
  }, []);

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center max-w-md p-6">
        {error ? (
          <>
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {error}
            </div>
            <button
              className="btn-primary px-6 py-2"
              onClick={() => { setError(null); startLogin().catch((err) => setError(err instanceof Error ? err.message : 'Failed to start login')); }}
            >
              Retry
            </button>
          </>
        ) : (
          <>
            <LoadingSpinner size="lg" />
            <p className="text-gray-600 mt-4">Redirecting to sign in...</p>
          </>
        )}
      </div>
    </div>
  );
}

/** Renders nothing — just connects the Central foundation event stream. */
function CentralEventStreamBridge() {
  useCentralEventStream();
  return null;
}

/** Renders nothing — just connects the Central notification SSE stream. */
function NotificationSSEBridge() {
  useNotificationSSE();
  return null;
}

function App() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Always allow access to auth callback route (pre-auth OAuth landing)
  if (location.pathname === '/auth/callback') {
    return (
      <Routes>
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
      </Routes>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // ALL authenticated routes — including diagnostics — are BootGate-wrapped
  // Only /auth/callback is exempt (pre-auth OAuth landing)
  return (
    <SuiteShellProvider adapter={SHELL_ADAPTER}>
      <BootGate>
        <Routes>
          {/* Diagnostics inside BootGate but outside shell (no sidebar needed) */}
          <Route path="/central-diagnostics" element={<CentralDiagnosticsPage />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          {/* Shell-wrapped routes: SuiteShell uses <Outlet /> for content */}
          <Route element={<SuiteShell />}>
            <Route path="/dashboard" element={<ErrorBoundary><DashboardPage /></ErrorBoundary>} />
            <Route path="/recommendations" element={<ErrorBoundary><RecommendationsPage /></ErrorBoundary>} />
            <Route path="/plans" element={<ErrorBoundary><PlansPage /></ErrorBoundary>} />
            <Route path="/alerts" element={<ErrorBoundary><AlertsPage /></ErrorBoundary>} />
            <Route path="/agents" element={<ErrorBoundary><AgentsPage /></ErrorBoundary>} />
            <Route path="/chat" element={<ErrorBoundary><ChatPage /></ErrorBoundary>} />
            <Route path="/settings" element={<ErrorBoundary><SettingsPage /></ErrorBoundary>} />
            <Route path="/settings/:tab" element={<ErrorBoundary><SettingsPage /></ErrorBoundary>} />
            <Route path="/documents" element={<ErrorBoundary><DocumentsPage /></ErrorBoundary>} />
            <Route path="/documents/:id" element={<ErrorBoundary><DocumentDetailPage /></ErrorBoundary>} />
            <Route path="/beneficiaries" element={<ErrorBoundary><BeneficiaryReviewPage /></ErrorBoundary>} />
            <Route path="/offload-responsibilities" element={<ErrorBoundary><OffloadResponsibilitiesPage /></ErrorBoundary>} />
          </Route>

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        {/* Central foundation event stream — connects when boot is ready */}
        <CentralEventStreamBridge />
        {/* Central notification SSE — real-time inbox/unread updates */}
        <NotificationSSEBridge />
        {/* Dev-only debug panel for auth state verification */}
        <DevWhoAmI />
      </BootGate>
    </SuiteShellProvider>
  );
}

export default App;
